// Map transcript (source-video time) onto the edited timeline (trimmed / split / moved base clips).
const r3 = (n) => Math.round(n * 1000) / 1000;

export const baseClips = (mediaTracks = []) => mediaTracks
  .filter((t) => t.type === "video")
  .flatMap((t) => t.objects || [])
  .filter((o) => !o.asset_url && !o.asset_id && o.source_end != null)
  .sort((a, b) => (a.start || 0) - (b.start || 0));

export const isEdited = (clips, duration) => clips.length > 0 && !(clips.length === 1
  && Math.abs((clips[0].start || 0)) < 0.01 && Math.abs((clips[0].source_start || 0)) < 0.01
  && Math.abs((clips[0].source_end || 0) - duration) < 0.05 && Math.abs((clips[0].end || 0) - duration) < 0.05);

export function srcToTimeline(t, clips) {
  for (const c of clips) {
    const ss = c.source_start || 0, se = c.source_end;
    if (t >= ss - 1e-3 && t <= se + 1e-3) {
      const k = ((c.end || 0) - (c.start || 0)) / Math.max(1e-3, se - ss);
      return (c.start || 0) + (t - ss) * k;
    }
  }
  return null;
}

// Source word span -> timeline span, or null if the word's start was cut out. The end is clamped to its clip.
export function mapSpan(start, end, clips) {
  for (const c of clips) {
    const ss = c.source_start || 0, se = c.source_end;
    if (start >= ss - 1e-3 && start <= se + 1e-3) {
      const k = ((c.end || 0) - (c.start || 0)) / Math.max(1e-3, se - ss);
      const st = (c.start || 0) + (start - ss) * k;
      const en = (c.start || 0) + (Math.min(end, se) - ss) * k;
      return { start: r3(st), end: r3(Math.max(en, st + 0.05)) };
    }
  }
  return null;
}

// Re-times every transcript-backed caption to the edited video. Captions whose words were cut out are dropped.
// Returns { layers, moved, dropped }.
export function resyncCaptionLayers(layers, segments, mediaTracks) {
  const clips = baseClips(mediaTracks);
  const src = new Map((segments || []).flatMap((s) => s.words || []).filter((w) => w.word_id).map((w) => [w.word_id, w]));
  let moved = 0, dropped = 0;
  if (!clips.length || !src.size) return { layers, moved, dropped };
  const next = layers.map((l) => {
    if (l.type !== "captions" && l.type !== "text") return l;
    const objects = (l.objects || []).map((o) => {
      const ws = o.words && o.words.length ? o.words : (o.word_id ? [{ word_id: o.word_id, word: o.text, start: o.start, end: o.end }] : []);
      if (!ws.length || !ws.some((w) => src.has(w.word_id))) return o; // manual text: leave as is
      const mapped = ws.map((w) => {
        const sw = src.get(w.word_id) || w;
        const m = mapSpan(sw.start, sw.end, clips);
        return m ? { ...w, ...m } : null;
      }).filter(Boolean);
      if (!mapped.length) { dropped += 1; return null; }
      moved += 1;
      const start = mapped[0].start, end = mapped[mapped.length - 1].end;
      return { ...o, start, end, ...(o.words && o.words.length ? { words: mapped, ...(mapped.length !== ws.length ? { text: mapped.map((w) => w.word).join(" ") } : {}) } : {}) };
    }).filter(Boolean);
    return { ...l, objects };
  });
  return { layers: next, moved, dropped };
}

// Timeline time -> source time of the base video (null when t is not over a base clip).
export function tlToSrc(t, clips) {
  if (!clips.length) return t;
  for (const c of clips) {
    if (t >= (c.start || 0) - 1e-3 && t <= (c.end || 0) + 1e-3) {
      const k = (c.source_end - (c.source_start || 0)) / Math.max(1e-3, (c.end || 0) - (c.start || 0));
      return (c.source_start || 0) + (t - (c.start || 0)) * k;
    }
  }
  return null;
}
